import React from "react";
import { CellStyle, CellBorders } from "../../data/api/trialBalanceApi";

/**
 * Convert CellStyle to React.CSSProperties
 */
export function cellStyleToCSS(style?: CellStyle): React.CSSProperties {
  if (!style) return {};

  const css: React.CSSProperties = {};

  // Font properties
  if (style.fontWeight) {
    css.fontWeight = style.fontWeight as React.CSSProperties["fontWeight"];
  }
  if (style.fontStyle) {
    css.fontStyle = style.fontStyle as React.CSSProperties["fontStyle"];
  }
  if (style.textDecoration) {
    css.textDecoration = style.textDecoration;
  }
  if (style.color) {
    css.color = style.color;
  }
  if (style.fontSize) {
    css.fontSize = style.fontSize;
  }

  // Background color
  if (style.backgroundColor) {
    css.backgroundColor = style.backgroundColor;
  }

  // Alignment
  if (style.textAlign) {
    css.textAlign = style.textAlign as React.CSSProperties["textAlign"];
  }
  if (style.verticalAlign) {
    css.verticalAlign = style.verticalAlign as React.CSSProperties["verticalAlign"];
  }

  // Borders
  if (style.borders) {
    const borders = style.borders;
    if (borders.top) {
      css.borderTop = `${borders.top.style} ${borders.top.color}`;
    }
    if (borders.bottom) {
      css.borderBottom = `${borders.bottom.style} ${borders.bottom.color}`;
    }
    if (borders.left) {
      css.borderLeft = `${borders.left.style} ${borders.left.color}`;
    }
    if (borders.right) {
      css.borderRight = `${borders.right.style} ${borders.right.color}`;
    }
  }

  return css;
}

/**
 * Format a value using Excel number format code
 * This is a simplified implementation for common formats
 */
export function formatWithNumberFormat(
  value: string | number | null,
  format?: string
): string {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (typeof value === "number" && Object.is(value, -0)) {
    value = 0;
  }

  // Show zero as hyphen (accounting convention)
  if (typeof value === "number" && value === 0) {
    return "-";
  }

  // If no format or not a number, return as-is (with brackets for negatives). Use 2 decimal places.
  if (!format || typeof value !== "number") {
    if (typeof value === "number") {
      const opts = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      };
      const formatted = new Intl.NumberFormat("en-IN", opts).format(Math.abs(value));
      return value < 0 ? `(${formatted})` : formatted;
    }
    return String(value);
  }

  const activeFormatSection = getActiveFormatSection(format, value);
  const normalizedFormatSection = normalizeFormatSection(activeFormatSection);
  const formatLower = normalizedFormatSection.toLowerCase();

  // Percentage formats - parse decimals from the active section only
  if (formatLower.includes("%")) {
    const decimals = getDecimalPlacesForPercentFormat(normalizedFormatSection);
    const scaled = value * 100;
    const normalizedScaled = Object.is(scaled, -0) ? 0 : scaled;
    const formatted = new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(Math.abs(scaled));
    return value < 0 ? `(${formatted}%)` : `${formatted}%`;
  }

  // Currency formats (basic)
  if (
    normalizedFormatSection.includes("$") ||
    normalizedFormatSection.includes("\u20B9")
  ) {
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const decimals = getDecimalPlacesForNumberFormat(normalizedFormatSection, 2);
    const formatted = new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(absValue);
    const symbol = normalizedFormatSection.includes("\u20B9") ? "\u20B9" : "$";
    return isNegative ? `(${symbol}${formatted})` : `${symbol}${formatted}`;
  }

  // Accounting formats (parentheses for negatives)
  if (
    normalizedFormatSection.includes("(") &&
    normalizedFormatSection.includes(")")
  ) {
    const decimals = getDecimalPlacesForNumberFormat(normalizedFormatSection, 2);
    const opts = { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
    if (value < 0) {
      return `(${new Intl.NumberFormat("en-IN", opts).format(Math.abs(value))})`;
    }
    return new Intl.NumberFormat("en-IN", opts).format(value);
  }

  // Decimal places (brackets for negatives)
  const decimalMatch = normalizedFormatSection.match(/\.([0#?]+)/);
  if (decimalMatch) {
    const decimals = decimalMatch[1].replace(/[^0#?]/g, "").length;
    const opts = {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    };
    const formatted = new Intl.NumberFormat("en-IN", opts).format(Math.abs(value));
    return value < 0 ? `(${formatted})` : formatted;
  }

  // Default: honor the active section decimal placeholders, or 2 decimals for report numbers; brackets for negatives
  const defaultDecimals = getDecimalPlacesForNumberFormat(
    normalizedFormatSection,
    2
  );
  const defaultOpts = {
    minimumFractionDigits: defaultDecimals,
    maximumFractionDigits: defaultDecimals,
  };
  const defaultFormatted = new Intl.NumberFormat("en-IN", defaultOpts).format(
    Math.abs(value)
  );
  return value < 0 ? `(${defaultFormatted})` : defaultFormatted;
}

function getActiveFormatSection(format: string, value: number): string {
  const sections = format.split(";");
  if (sections.length <= 1) {
    return format;
  }

  if (value > 0) {
    return sections[0] || format;
  }
  if (value < 0) {
    return sections[1] || sections[0] || format;
  }
  return sections[2] || sections[0] || format;
}

function normalizeFormatSection(section: string): string {
  // Remove quoted strings and escaped characters so placeholder parsing is reliable
  return section
    .replace(/"[^"]*"/g, "")
    .replace(/\\./g, "")
    .replace(/\[[^\]]*]/g, "")
    .trim();
}

function getDecimalPlacesForPercentFormat(formatSection: string): number {
  const beforePercent = formatSection.split("%")[0] || "";
  const decimalPart = beforePercent.split(".")[1];
  if (!decimalPart) {
    return 0;
  }
  return (decimalPart.match(/[0#?]/g) || []).length;
}

function getDecimalPlacesForNumberFormat(
  formatSection: string,
  fallback: number
): number {
  const decimalMatch = formatSection.match(/\.([0#?]+)/);
  if (!decimalMatch) {
    return fallback;
  }
  return (decimalMatch[1].match(/[0#?]/g) || []).length;
}

/**
 * Merged cell information
 */
export interface MergedCellInfo {
  colSpan: number;
  rowSpan: number;
  isHidden: boolean; // True if this cell is covered by a merge
  masterCell?: string; // The top-left cell of the merge range
}

/**
 * Parse merged cell ranges into a lookup map
 * Returns a map of cellAddress -> MergedCellInfo
 */
export function parseMergedCells(
  mergedRanges?: string[]
): Map<string, MergedCellInfo> {
  const map = new Map<string, MergedCellInfo>();

  if (!mergedRanges || mergedRanges.length === 0) {
    return map;
  }

  for (const range of mergedRanges) {
    // Parse range like "A1:B3"
    const match = range.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
    if (!match) continue;

    const [, startCol, startRowStr, endCol, endRowStr] = match;
    const startRow = parseInt(startRowStr, 10);
    const endRow = parseInt(endRowStr, 10);

    const startColIndex = columnToIndex(startCol);
    const endColIndex = columnToIndex(endCol);

    const colSpan = endColIndex - startColIndex + 1;
    const rowSpan = endRow - startRow + 1;

    const masterCell = `${startCol}${startRow}`;

    // Mark the master cell (top-left)
    map.set(masterCell, {
      colSpan,
      rowSpan,
      isHidden: false,
      masterCell,
    });

    // Mark all other cells in the range as hidden
    for (let row = startRow; row <= endRow; row++) {
      for (let colIdx = startColIndex; colIdx <= endColIndex; colIdx++) {
        const cellAddress = `${indexToColumn(colIdx)}${row}`;
        if (cellAddress !== masterCell) {
          map.set(cellAddress, {
            colSpan: 1,
            rowSpan: 1,
            isHidden: true,
            masterCell,
          });
        }
      }
    }
  }

  return map;
}

/**
 * Convert column letter to 1-based index (A=1, B=2, ..., Z=26, AA=27)
 */
function columnToIndex(col: string): number {
  let index = 0;
  for (let i = 0; i < col.length; i++) {
    index = index * 26 + (col.charCodeAt(i) - 64);
  }
  return index;
}

/**
 * Convert 1-based index to column letter
 */
function indexToColumn(index: number): string {
  let result = "";
  while (index > 0) {
    const remainder = (index - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    index = Math.floor((index - 1) / 26);
  }
  return result;
}
